import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/shared/stores/authStore'
import { useWarRoomStore } from '../stores/warRoomStore'
import { WAR_ROOM_QUERY_KEYS } from '../api/queryClient'
import type { WSStatus } from '../api/wsClient'
import type { WarEvent } from '../types/event.types'

export interface UseWarRoomWSOptions {
  sessionId: string
  teamId?: string
}

export interface UseWarRoomWSReturn {
  wsStatus: WSStatus
}

export function invalidateForEvent(
  evt: WarEvent,
  sessionId: string,
  queryClient: QueryClient
): void {
  switch (evt.event_type) {
    case 'step.updated':
    case 'step.unlocked':
    case 'step.timeout':
      queryClient.invalidateQueries({
        queryKey: WAR_ROOM_QUERY_KEYS.steps(sessionId),
      })
      break

    case 'session.started':
    case 'session.paused':
    case 'session.completed':
    case 'session.failed':
    case 'session.rolled_back':
      queryClient.invalidateQueries({
        queryKey: WAR_ROOM_QUERY_KEYS.session(sessionId),
      })
      break

    case 'gonogo.voted':
    case 'gonogo.resolved':
      queryClient.invalidateQueries({
        queryKey: WAR_ROOM_QUERY_KEYS.teams(sessionId),
      })
      break

    case 'preflight.checked':
    case 'preflight.failed':
      queryClient.invalidateQueries({
        queryKey: WAR_ROOM_QUERY_KEYS.preflight(sessionId),
      })
      break

    default:
      break
  }
}

/**
 * Manages WebSocket lifecycle for a war room session page.
 * Bridges WS events from warRoomStore to TanStack Query cache invalidations.
 */
export function useWarRoomWS({ sessionId, teamId }: UseWarRoomWSOptions): UseWarRoomWSReturn {
  const queryClient = useQueryClient()
  const connect = useWarRoomStore((s) => s.connect)
  const disconnect = useWarRoomStore((s) => s.disconnect)
  const wsStatus = useWarRoomStore((s) => s.wsStatus)
  const token = useAuthStore((s) => (s as any).accessToken ?? s.token)

  // Stable ref to avoid stale closure in subscribe callback
  const sessionIdRef = useRef(sessionId)
  sessionIdRef.current = sessionId

  // useEffect — connect on mount, disconnect on unmount
  useEffect(() => {
    if (!token) return
    connect(sessionId, teamId, token)
    return () => {
      disconnect()
    }
  }, [sessionId, teamId, token, connect, disconnect])

  // Subscribe to warRoomStore events for cache invalidation
  // Uses subscribeWithSelector to observe recentEvents
  useEffect(() => {
    const unsubscribe = useWarRoomStore.subscribe(
      (state) => state.recentEvents,
      (recentEvents, prevRecentEvents) => {
        if (recentEvents.length === prevRecentEvents.length) return
        const latestEntry = recentEvents[0]
        if (!latestEntry) return
        invalidateForEvent(latestEntry.event, sessionIdRef.current, queryClient)
      }
    )
    return unsubscribe
  }, [queryClient])

  return { wsStatus }
}
